import type { CastAssignment, CastingRole } from '../../personnel/types';
import type { Act, Character, Scene, ScriptBlock } from '../types';
import { orderScriptBlocksByShowStructure } from './script-block-order';
import { getOrderedScenes } from './scene-order';

export interface DeriveDefaultRehearsalCharacterIdsInput {
    personnelId?: string | null;
    assignments: CastAssignment[];
    characters: Character[];
}

export interface RehearsalCharacterColor {
    id: string;
    rail: string;
    background: string;
    foreground: string;
}

export interface RehearsalCharacterSelection {
    characterId: string;
    role: CastingRole;
}

export interface RehearsalSpeechTarget {
    id: string;
    type: 'cue' | 'fallback';
    characterId: string;
    characterName: string;
    label: string;
    cueBlockId?: string;
    startBlockId: string;
    pageNumber: number;
    blockIds: string[];
}

export interface RehearsalScriptPage {
    page: number;
    sceneLabel?: string;
    blocks: ScriptBlock[];
}

const REHEARSAL_CHARACTER_COLORS: RehearsalCharacterColor[] = [
    {
        id: 'blue',
        rail: 'var(--color-blue-500, #3b82f6)',
        background: 'var(--color-blue-50, #eff6ff)',
        foreground: 'var(--color-blue-700, #1d4ed8)',
    },
    {
        id: 'emerald',
        rail: 'var(--color-emerald-500, #10b981)',
        background: 'var(--color-emerald-50, #ecfdf5)',
        foreground: 'var(--color-emerald-700, #047857)',
    },
    {
        id: 'amber',
        rail: 'var(--color-amber-500, #f59e0b)',
        background: 'var(--color-amber-50, #fffbeb)',
        foreground: 'var(--color-amber-700, #b45309)',
    },
    {
        id: 'rose',
        rail: 'var(--color-rose-500, #f43f5e)',
        background: 'var(--color-rose-50, #fff1f2)',
        foreground: 'var(--color-rose-700, #be123c)',
    },
    {
        id: 'violet',
        rail: 'var(--color-violet-500, #8b5cf6)',
        background: 'var(--color-violet-50, #f5f3ff)',
        foreground: 'var(--color-violet-700, #6d28d9)',
    },
    {
        id: 'cyan',
        rail: 'var(--color-cyan-500, #06b6d4)',
        background: 'var(--color-cyan-50, #ecfeff)',
        foreground: 'var(--color-cyan-700, #0e7490)',
    },
];

const REHEARSAL_ROLE_COLORS: Record<CastingRole, RehearsalCharacterColor> = {
    primary: REHEARSAL_CHARACTER_COLORS[0]!,
    understudy: REHEARSAL_CHARACTER_COLORS[2]!,
    swing: REHEARSAL_CHARACTER_COLORS[1]!,
    standby: REHEARSAL_CHARACTER_COLORS[4]!,
};

const CASTING_ROLE_PRIORITY: Record<CastingRole, number> = {
    primary: 0,
    understudy: 1,
    swing: 2,
    standby: 3,
};

export function deriveDefaultRehearsalCharacterIds(
    input: DeriveDefaultRehearsalCharacterIdsInput
): string[] {
    return deriveDefaultRehearsalCharacterSelections(input).map(selection => selection.characterId);
}

export function deriveDefaultRehearsalCharacterSelections(
    input: DeriveDefaultRehearsalCharacterIdsInput
): RehearsalCharacterSelection[] {
    if (!input.personnelId) return [];

    const activeCharacterIds = new Set(
        input.characters
            .filter(character => !character.deleted)
            .map(character => character.id)
    );
    const selectedCharacterIds: string[] = [];
    const selectedRoleByCharacterId = new Map<string, CastingRole>();

    for (const assignment of input.assignments) {
        if (assignment.personnelId !== input.personnelId || !activeCharacterIds.has(assignment.characterId)) {
            continue;
        }

        const existingRole = selectedRoleByCharacterId.get(assignment.characterId);
        if (existingRole) {
            selectedRoleByCharacterId.set(
                assignment.characterId,
                getPreferredCastingRole(existingRole, assignment.role)
            );
            continue;
        }

        selectedCharacterIds.push(assignment.characterId);
        selectedRoleByCharacterId.set(assignment.characterId, assignment.role);
    }

    return selectedCharacterIds.map(characterId => ({
        characterId,
        role: selectedRoleByCharacterId.get(characterId)!,
    }));
}

export function getRehearsalCharacterColorMap(
    characterIds: string[],
    roleByCharacterId?: ReadonlyMap<string, CastingRole>
): Map<string, RehearsalCharacterColor> {
    const colorMap = new Map<string, RehearsalCharacterColor>();
    const usedColorIds = new Set<string>();
    let colorIndex = 0;

    for (const characterId of characterIds) {
        if (colorMap.has(characterId)) continue;

        const role = roleByCharacterId?.get(characterId);
        const color = role
            ? REHEARSAL_ROLE_COLORS[role]
            : getNextFallbackColor(usedColorIds, colorIndex);

        colorMap.set(characterId, color);
        usedColorIds.add(color.id);
        if (!role) colorIndex += 1;
    }

    return colorMap;
}

export function orderRehearsalScriptBlocks(
    scriptBlocks: ScriptBlock[],
    acts: Act[],
    scenes: Scene[]
): ScriptBlock[] {
    return orderScriptBlocksByShowStructure(scriptBlocks, acts, scenes);
}

export function buildRehearsalSpeechTargets(
    orderedBlocks: ScriptBlock[],
    selectedCharacterIds: string[],
    characters: Character[]
): RehearsalSpeechTarget[] {
    const selectedCharacterIdSet = new Set(selectedCharacterIds);
    if (selectedCharacterIdSet.size === 0) return [];

    const characterNameById = new Map(
        characters
            .filter(character => !character.deleted)
            .map(character => [character.id, character.name] as const)
    );
    const coveredBlockIds = new Set<string>();
    const indexedTargets: Array<RehearsalSpeechTarget & { orderIndex: number }> = [];

    orderedBlocks.forEach((block, index) => {
        const characterId = block.characterId;
        if (
            block.type !== 'character_name'
            || !characterId
            || !selectedCharacterIdSet.has(characterId)
        ) {
            return;
        }

        const blockIds = [block.id];
        for (let nextIndex = index + 1; nextIndex < orderedBlocks.length; nextIndex += 1) {
            const nextBlock = orderedBlocks[nextIndex]!;
            if (nextBlock.type === 'character_name' || nextBlock.type === 'heading') {
                break;
            }

            if (isSpeechBlock(nextBlock) && nextBlock.characterId === characterId) {
                blockIds.push(nextBlock.id);
            }
        }

        for (const blockId of blockIds) {
            coveredBlockIds.add(blockId);
        }
        indexedTargets.push({
            id: block.id,
            type: 'cue',
            characterId,
            characterName: getCharacterName(characterId, characterNameById),
            label: getCharacterName(characterId, characterNameById),
            cueBlockId: block.id,
            startBlockId: block.id,
            pageNumber: block.pageNumber,
            blockIds,
            orderIndex: index,
        });
    });

    let pendingFallback: {
        characterId: string;
        firstBlock: ScriptBlock;
        blockIds: string[];
        orderIndex: number;
    } | null = null;

    const flushFallback = () => {
        if (!pendingFallback) return;

        const characterName = getCharacterName(pendingFallback.characterId, characterNameById);
        indexedTargets.push({
            id: `fallback-${pendingFallback.firstBlock.id}`,
            type: 'fallback',
            characterId: pendingFallback.characterId,
            characterName,
            label: characterName,
            startBlockId: pendingFallback.firstBlock.id,
            pageNumber: pendingFallback.firstBlock.pageNumber,
            blockIds: pendingFallback.blockIds,
            orderIndex: pendingFallback.orderIndex,
        });
        pendingFallback = null;
    };

    orderedBlocks.forEach((block, index) => {
        if (block.type === 'character_name' || block.type === 'heading') {
            flushFallback();
            return;
        }

        if (!isSpeechBlock(block)) {
            return;
        }

        const characterId = block.characterId;
        if (
            !characterId
            || !selectedCharacterIdSet.has(characterId)
            || coveredBlockIds.has(block.id)
        ) {
            flushFallback();
            return;
        }

        if (pendingFallback?.characterId === characterId) {
            pendingFallback.blockIds.push(block.id);
            return;
        }

        flushFallback();
        pendingFallback = {
            characterId,
            firstBlock: block,
            blockIds: [block.id],
            orderIndex: index,
        };
    });
    flushFallback();

    return indexedTargets
        .toSorted((left, right) => left.orderIndex - right.orderIndex)
        .map(({ orderIndex: _orderIndex, ...target }) => target);
}

export function groupRehearsalScriptPages(
    orderedBlocks: ScriptBlock[],
    acts: Act[],
    scenes: Scene[]
): RehearsalScriptPage[] {
    const sceneLabelById = buildSceneLabelMap(acts, scenes);
    const pages = new Map<number, RehearsalScriptPage>();

    for (const block of orderedBlocks) {
        if (block.type === 'front_matter') continue;

        const page = pages.get(block.pageNumber) || {
            page: block.pageNumber,
            blocks: [],
        };

        page.blocks.push(block);
        if (!page.sceneLabel && block.sceneId) {
            page.sceneLabel = sceneLabelById.get(block.sceneId);
        }
        pages.set(block.pageNumber, page);
    }

    return Array.from(pages.values());
}

function isSpeechBlock(block: ScriptBlock): boolean {
    return block.type === 'dialogue' || block.type === 'lyric';
}

function getPreferredCastingRole(currentRole: CastingRole, nextRole: CastingRole): CastingRole {
    return CASTING_ROLE_PRIORITY[nextRole] < CASTING_ROLE_PRIORITY[currentRole]
        ? nextRole
        : currentRole;
}

function getNextFallbackColor(
    usedColorIds: ReadonlySet<string>,
    startIndex: number
): RehearsalCharacterColor {
    for (let offset = 0; offset < REHEARSAL_CHARACTER_COLORS.length; offset += 1) {
        const color = REHEARSAL_CHARACTER_COLORS[(startIndex + offset) % REHEARSAL_CHARACTER_COLORS.length]!;
        if (!usedColorIds.has(color.id)) return color;
    }
    return REHEARSAL_CHARACTER_COLORS[startIndex % REHEARSAL_CHARACTER_COLORS.length]!;
}

function getCharacterName(
    characterId: string,
    characterNameById: ReadonlyMap<string, string>
): string {
    return characterNameById.get(characterId) || 'Character';
}

function buildSceneLabelMap(acts: Act[], scenes: Scene[]): Map<string, string> {
    return new Map(
        getOrderedScenes(acts, scenes, { includePreset: true }).map(scene => {
            const label = scene.actName && !isPresetAct(scene.actId, acts)
                ? `${scene.actName} - ${scene.name}`
                : scene.name;
            return [scene.id, label] as const;
        })
    );
}

function isPresetAct(actId: string | null, acts: Act[]): boolean {
    if (!actId) return false;
    return acts.find(act => act.id === actId)?.isPreset ?? false;
}
